import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Check, PencilLine, RotateCcw, Type, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "./auth";
import { useLocation } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TEXT_OVERRIDES_STORAGE_KEY = "viscar-temp-text-overrides-v2";
const TEXT_EDIT_MODE_STORAGE_KEY = "viscar-temp-text-edit-mode-v2";

const TextEditorContext = createContext(null);

const sanitizeTextId = (textId) => textId.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export const TextEditorProvider = ({ children }) => {
  const { user } = useAuth();
  const [isEditMode, setIsEditMode] = useState(() => window.sessionStorage.getItem(TEXT_EDIT_MODE_STORAGE_KEY) === "true");
  const [isSaving, setIsSaving] = useState(false);
  const [overrides, setOverrides] = useState(() => {
    const stored = window.sessionStorage.getItem(TEXT_OVERRIDES_STORAGE_KEY);
    if (!stored) {
      return {};
    }

    try {
      return JSON.parse(stored);
    } catch {
      return {};
    }
  });

  useEffect(() => {
    window.sessionStorage.setItem(TEXT_EDIT_MODE_STORAGE_KEY, String(isEditMode));
    document.body.classList.toggle("text-edit-mode-active", isEditMode);
    return () => document.body.classList.remove("text-edit-mode-active");
  }, [isEditMode]);

  useEffect(() => {
    window.sessionStorage.setItem(TEXT_OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
  }, [overrides]);

  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/viscar/text-config`);
        if (!response.ok) {
          throw new Error("Load text config failed");
        }

        const data = await response.json();
        setOverrides(data.text_overrides || {});
      } catch {
        toast.error("Non sono riuscito a caricare i testi salvati");
      }
    };

    loadOverrides();
  }, []);

  const persistOverrides = async (nextOverrides, successMessage) => {
    setIsSaving(true);

    try {
      const response = await fetch(`${API_BASE}/api/viscar/text-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text_overrides: nextOverrides }),
      });

      if (!response.ok) {
        throw new Error("Save text config failed");
      }

      const data = await response.json();
      setOverrides(data.text_overrides || {});
      window.sessionStorage.setItem(TEXT_OVERRIDES_STORAGE_KEY, JSON.stringify(data.text_overrides || {}));
      toast.success(successMessage);
      return true;
    } catch {
      toast.error("Non sono riuscito a salvare i testi");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const value = useMemo(() => ({
    isEditMode: user?.role === "admin" ? isEditMode : false,
    canEdit: user?.role === "admin",
    isSaving,
    setIsEditMode,
    getText: (textId, fallback) => overrides[textId] ?? fallback,
    updateText: async (textId, nextValue, fallback) => {
      const nextOverrides = (() => {
        if (nextValue === fallback) {
          const updated = { ...overrides };
          delete updated[textId];
          return updated;
        }

        return {
          ...overrides,
          [textId]: nextValue,
        };
      })();

      return persistOverrides(nextOverrides, "Testo salvato");
    },
    resetTexts: async () => {
      return persistOverrides({}, "Testi ripristinati");
    },
  }), [isEditMode, isSaving, overrides, user?.role]);

  return <TextEditorContext.Provider value={value}>{children}</TextEditorContext.Provider>;
};

export const useTextEditor = () => {
  const context = useContext(TextEditorContext);
  if (!context) {
    throw new Error("useTextEditor must be used inside TextEditorProvider");
  }
  return context;
};

export const EditableText = ({
  as: Component = "span",
  className = "",
  defaultText,
  inline = false,
  multiline = false,
  mode = "button",
  textId,
  testId,
}) => {
  const { getText, isEditMode, isSaving, updateText } = useTextEditor();
  const resolvedText = getText(textId, defaultText);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(resolvedText);
  const sanitizedId = sanitizeTextId(textId);

  useEffect(() => {
    if (!isEditing) {
      setDraft(resolvedText);
    }
  }, [isEditing, resolvedText]);

  const handleSave = async () => {
    const saved = await updateText(textId, draft, defaultText);
    if (saved) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setDraft(resolvedText);
    setIsEditing(false);
  };

  const handleInteractiveOpen = (event) => {
    if (!isEditMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <span data-testid={testId || `editable-text-${sanitizedId}`} className={`editable-text-shell ${inline ? "is-inline" : "is-block"}`}>
        {multiline ? (
          <textarea
            data-testid={`editable-text-input-${sanitizedId}`}
            className="editable-text-field text-editor-field"
            rows={4}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        ) : (
          <input
            data-testid={`editable-text-input-${sanitizedId}`}
            className="editable-text-field text-editor-field"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        )}
        <span className="editable-text-actions">
          <button data-testid={`editable-text-save-${sanitizedId}`} type="button" className="editable-text-control text-editor-control" onClick={handleSave} disabled={isSaving}>
            <Check size={14} /> {isSaving ? "Salvataggio..." : "Salva"}
          </button>
          <button data-testid={`editable-text-cancel-${sanitizedId}`} type="button" className="editable-text-control text-editor-control" onClick={handleCancel} disabled={isSaving}>
            <X size={14} /> Annulla
          </button>
        </span>
      </span>
    );
  }

  if (isEditMode && mode === "click") {
    return (
      <Component
        data-testid={testId || `editable-text-${sanitizedId}`}
        className={`${className} editable-text-clickable`}
        onClick={handleInteractiveOpen}
        title="Clicca per modificare questo testo"
      >
        {resolvedText}
      </Component>
    );
  }

  if (isEditMode) {
    return (
      <span data-testid={testId || `editable-text-${sanitizedId}`} className={`editable-text-shell ${inline ? "is-inline" : "is-block"}`}>
        <Component className={className}>{resolvedText}</Component>
        <button
          data-testid={`editable-text-open-${sanitizedId}`}
          type="button"
          className="editable-text-control text-editor-control"
          onClick={() => setIsEditing(true)}
        >
          <PencilLine size={14} /> Edit
        </button>
      </span>
    );
  }

  return <Component data-testid={testId} className={className}>{resolvedText}</Component>;
};

export const TextEditorControls = () => {
  const { canEdit, isEditMode, isSaving, resetTexts, setIsEditMode } = useTextEditor();
  const location = useLocation();

  if (!canEdit || location.pathname.startsWith("/area-riservata")) return null;

  return (
    <div data-testid="text-editor-controls" className="text-editor-controls">
      <button
        data-testid="text-editor-toggle-button"
        type="button"
        className="text-editor-floating-button text-editor-control"
        onClick={() => setIsEditMode((current) => !current)}
      >
        <Type size={16} /> {isEditMode ? "Esci da modifica" : "Modalità modifica testi"}
      </button>

      {isEditMode ? (
        <button
          data-testid="text-editor-reset-button"
          type="button"
          className="text-editor-floating-button text-editor-floating-button--ghost text-editor-control"
          onClick={resetTexts}
          disabled={isSaving}
        >
          <RotateCcw size={16} /> {isSaving ? "Salvataggio..." : "Ripristina testi salvati"}
        </button>
      ) : null}
    </div>
  );
};